/**
 * ProfilePage — User Profile
 * 
 * v2 Changes:
 * - Zone filter + sort now read from PlatformContext (persist Map ↔ Explore ↔ here)
 * - Zone chips call platform.filters.toggleZone instead of local setState
 * - Sort dropdown maps platform sortBy to API sort params
 * - Server-side project fetch still happens per-user — platform provides the filter state
 *
 * Place at: src/pages/ProfilePage.jsx
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Calendar, GitFork, Eye, Star, Users, UserPlus, UserMinus,
  Settings, ExternalLink, Award, MessageCircle, Sparkles, ChevronDown,
  Grid, List, Filter, TrendingUp, Zap, Heart, Package, Palette, Music,
  BookOpen, Cpu, DollarSign, Volume2, Edit3, Share2, MoreHorizontal,
  CheckCircle, Clock, Lightbulb, X, Target, Wrench, HeartHandshake,
  Compass, Link2, GraduationCap, Plus, Utensils, Stethoscope,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { Button, Badge, Spinner } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { usePlatform } from '../context/PlatformContext';
import { api } from '../api';
import { ZONES, getProjectZone } from '../config/zones';
import { API_ORIGIN } from '../config/apiBase';
import InfluenceGraph from '../components/profile/InfluenceGraph';

// ============================================================================
// ZONE ICON MAP
// ============================================================================

const ZONE_ICONS = {
  build: Package,
  beautiful: Palette,
  sound: Volume2,
  taste: Utensils,
  story: BookOpen,
  heal: Stethoscope,
  smart: Cpu,
  sell: TrendingUp,
};

// ============================================================================
// HELPERS
// ============================================================================

const formatDate = (dateStr) => {
  if (!dateStr) return ''; 
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const formatNumber = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

const getYearLabel = (year) => {
  const labels = { 1: 'Freshman', 2: 'Sophomore', 3: 'Junior', 4: 'Senior', 5: 'Graduate' };
  return labels[year] || (year > 5 ? 'Graduate' : null);
};

// ============================================================================
// ZONE DISTRIBUTION BAR
// ============================================================================

const ZoneBar = ({ zoneStats, totalProjects }) => {
  if (!zoneStats || Object.keys(zoneStats).length === 0) return null;
  
  const sorted = Object.entries(zoneStats)
    .filter(([_, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);
  
  if (sorted.length === 0) return null;
  
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Creative Zones</h3>
      <div className="flex h-2 rounded-full overflow-hidden bg-zinc-800/50">
        {sorted.map(([zone, count]) => {
          const zoneConfig = ZONES[zone];
          const pct = totalProjects > 0 ? (count / totalProjects) * 100 : 0;
          return (
            <div
              key={zone}
              className="h-full transition-all duration-500"
              style={{ 
                width: `${Math.max(pct, 2)}%`, 
                backgroundColor: zoneConfig?.color || '#71717a' 
              }}
              title={`${zoneConfig?.label || zone}: ${count} projects (${Math.round(pct)}%)`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {sorted.map(([zone, count]) => {
          const zoneConfig = ZONES[zone];
          return (
            <span key={zone} className="flex items-center gap-1.5 text-xs text-zinc-400">
              <span 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: zoneConfig?.color || '#71717a' }} 
              />
              {zoneConfig?.shortLabel || zone}
              <span className="text-zinc-600">{count}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// STAT CARD
// ============================================================================

const StatItem = ({ icon: Icon, label, value, color = 'text-zinc-400' }) => (
  <div className="flex flex-col items-center gap-1 px-3 py-2">
    <div className="flex items-center gap-1.5">
      <Icon className={cn('w-3.5 h-3.5', color)} />
      <span className="text-lg font-bold text-white">{formatNumber(value)}</span>
    </div>
    <span className="text-[11px] text-zinc-500 font-medium">{label}</span>
  </div>
);

// ============================================================================
// PROJECT CARD
// ============================================================================

const ProjectCard = ({ project, onNavigate }) => {
  // Was ZONES[project.category] — but `category` is a free-text field the user
  // types at creation ("Science", "woodworking") or leaves empty. 29 of 34
  // production rows are NULL, so ZONES[...] was undefined and the zone badge
  // below never rendered for anyone.
  //
  // getProjectZone() is the same function the cosmic map and the project
  // overlay use, so the badge here now agrees with where the node sits.
  const zoneConfig = getProjectZone(project);
  const ZoneIcon = ZONE_ICONS[zoneConfig?.id] || Sparkles;
  
  return (
    <button
      onClick={() => onNavigate(project.id)}
      className="group relative bg-zinc-900/60 border border-zinc-800/60 rounded-2xl overflow-hidden text-left transition-all duration-300 hover:border-zinc-700/80 hover:bg-zinc-900/80 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
    >
      {/* Cover Image */}
      <div className="aspect-[4/3] bg-zinc-800/50 relative overflow-hidden">
        {project.imageUrl ? (
          <img 
            src={project.imageUrl.startsWith('http') ? project.imageUrl : `${API_ORIGIN}${project.imageUrl}`}
            alt={project.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ZoneIcon 
              className="w-12 h-12 opacity-20" 
              style={{ color: zoneConfig?.color || '#71717a' }} 
            />
          </div>
        )}
        
        {/* Zone badge */}
        {zoneConfig && (
          <div 
            className="absolute top-3 left-3 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider backdrop-blur-md"
            style={{ 
              backgroundColor: `${zoneConfig.color}20`, 
              color: zoneConfig.color,
              border: `1px solid ${zoneConfig.color}30`
            }}
          >
            {zoneConfig.shortLabel}
          </div>
        )}

        {/* Type badge */}
        <div className={cn(
          'absolute top-3 right-3 w-2.5 h-2.5 rounded-full ring-2 ring-zinc-900',
          project.projectType === 'physical' ? 'bg-emerald-400' :
          project.projectType === 'digital' ? 'bg-violet-400' : 'bg-amber-400'
        )} title={project.projectType} />
        
        {/* Price tag */}
        {project.isForSale && project.price && (
          <div className="absolute bottom-3 right-3 px-2 py-1 rounded-lg bg-emerald-500/90 text-white text-xs font-bold backdrop-blur-sm">
            ${project.price}
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="p-4">
        <h3 className="font-semibold text-white text-sm leading-tight mb-1 line-clamp-1 group-hover:text-purple-300 transition-colors">
          {project.name}
        </h3>
        {project.description && (
          <p className="text-xs text-zinc-500 line-clamp-2 mb-3 leading-relaxed">
            {project.description}
          </p>
        )}
        
        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {project.tags.slice(0, 3).map(tag => (
              <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded-md bg-zinc-800 text-zinc-500 font-medium">
                {tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span className="px-1.5 py-0.5 text-[10px] rounded-md bg-zinc-800 text-zinc-600">
                +{project.tags.length - 3}
              </span>
            )}
          </div>
        )}
        
        {/* Stats row — connection-centric */}
        <div className="flex items-center gap-3 text-[11px] text-zinc-600">
          {project.forkCount > 0 && (
            <span className="flex items-center gap-1">
              <GitFork className="w-3 h-3" /> {project.forkCount}
            </span>
          )}
          {(project.parentProjectId || project.isChallengeResponse) && (
            <span className="flex items-center gap-1 text-purple-500/70">
              <Lightbulb className="w-3 h-3" /> {project.isChallengeResponse ? 'Response' : 'Fork'}
            </span>
          )}
          <span className="ml-auto text-zinc-700">
            {formatDate(project.createdAt)}
          </span>
        </div>
      </div>
    </button>
  );
};

// ============================================================================
// FOLLOW BUTTON
// ============================================================================

const FollowButton = ({ isFollowing, onToggle, loading }) => {
  const [hovering, setHovering] = useState(false);
  
  if (isFollowing) {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={onToggle}
        disabled={loading}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={cn(
          'min-w-[100px] transition-all',
          hovering && 'border-red-500/50 text-red-400 hover:bg-red-500/10'
        )}
      >
        {hovering ? (
          <><UserMinus className="w-4 h-4" /> Unfollow</>
        ) : (
          <><CheckCircle className="w-4 h-4" /> Following</>
        )}
      </Button>
    );
  }
  
  return (
    <Button
      variant="primary"
      size="sm"
      onClick={onToggle}
      disabled={loading}
      className="min-w-[100px]"
    >
      <UserPlus className="w-4 h-4" /> Follow
    </Button>
  );
};

// ============================================================================
// COLLAB STYLE METADATA
// ============================================================================

const COLLAB_STYLES = {
  explorer: { label: 'Explorer', icon: Compass, desc: 'Loves discovering unexpected connections', color: '#06b6d4' },
  helper: { label: 'Helper', icon: HeartHandshake, desc: 'First to respond when someone needs a hand', color: '#22c55e' },
  creator: { label: 'Creator', icon: Sparkles, desc: 'Heads down building — forks welcome', color: '#a855f7' },
  connector: { label: 'Connector', icon: Link2, desc: 'Bridges people and ideas across zones', color: '#f59e0b' },
  mentor: { label: 'Mentor', icon: GraduationCap, desc: 'Helps others grow through shared work', color: '#ec4899' },
};

// ============================================================================
// CREATIVE IDENTITY CARD — Displayed prominently on profile
// ============================================================================

const CreativeIdentityCard = ({ identity, isOwnProfile, onEdit, connectionStats }) => {
  const navigate = useNavigate();
  if (!identity) return null;

  const { zones = [], collabStyle, skills = [], currentProject, openToCollab } = identity;
  const hasContent = zones.length > 0 || collabStyle || skills.length > 0 || currentProject;

  // If own profile with no identity set, show a prompt
  if (!hasContent && isOwnProfile) {
    return (
      <div className="mt-6 p-5 bg-zinc-900/60 border border-dashed border-zinc-700/50 rounded-2xl">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white mb-1">Set up your Creative Identity</h3>
            <p className="text-xs text-zinc-500 leading-relaxed mb-3">
              Tell others what you're into — your zones, skills, and collaboration style.
              This helps surface relevant challenges and connections for you.
            </p>
            <Button size="sm" onClick={onEdit} icon={Edit3}>
              Set Up Identity
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If viewing someone else with no identity, don't show anything
  if (!hasContent) return null;

  const style = collabStyle ? COLLAB_STYLES[collabStyle] : null;
  const StyleIcon = style?.icon || Sparkles;

  return (
    <div className="mt-6">
      {/* Section wrapper */}
      <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl overflow-hidden">

        {/* Zone band — colored strip at top showing their creative zones */}
        {zones.length > 0 && (
          <div className="flex h-1.5">
            {zones.map(zoneId => {
              const zone = ZONES[zoneId];
              return zone ? (
                <div
                  key={zoneId}
                  className="flex-1 transition-all"
                  style={{ backgroundColor: zone.color }}
                />
              ) : null;
            })}
          </div>
        )}

        <div className="p-5 space-y-4">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Creative Identity</h3>
            {isOwnProfile && (
              <button
                onClick={onEdit}
                className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-purple-400 transition-colors"
              >
                <Edit3 className="w-3 h-3" />
                Edit
              </button>
            )}
          </div>

          {/* Zones — big tappable badges → navigate to Zone Home */}
          {zones.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {zones.map(zoneId => {
                const zone = ZONES[zoneId];
                if (!zone) return null;
                const ZoneIcon = ZONE_ICONS[zoneId] || Sparkles;
                return (
                  <button
                    key={zoneId}
                    onClick={() => navigate(`/zone/${zoneId}`)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl border transition-all hover:brightness-125 active:scale-[0.97]"
                    style={{
                      backgroundColor: `${zone.color}12`,
                      borderColor: `${zone.color}30`,
                    }}
                  >
                    <ZoneIcon className="w-4 h-4" style={{ color: zone.color }} />
                    <span className="text-sm font-medium" style={{ color: zone.color }}>
                      {zone.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Collaboration style + Open to collab badge */}
          <div className="flex flex-wrap items-center gap-2">
            {style && (
              <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/60 border border-zinc-700/40 rounded-xl">
                <StyleIcon className="w-4 h-4" style={{ color: style.color }} />
                <div>
                  <span className="text-sm font-medium text-zinc-200">{style.label}</span>
                  <span className="text-[11px] text-zinc-500 ml-1.5 hidden sm:inline">{style.desc}</span>
                </div>
              </div>
            )}
            {openToCollab && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-500/10 border border-emerald-500/25 rounded-lg">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                <span className="text-xs font-medium text-emerald-400">Open to collaborate</span>
              </div>
            )}
          </div>

          {/* Currently working on */}
          {currentProject && (
            <div className="flex items-start gap-2.5 px-3.5 py-2.5 bg-zinc-800/40 rounded-xl">
              <Wrench className="w-3.5 h-3.5 text-zinc-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Currently working on</span>
                <p className="text-sm text-zinc-300 mt-0.5">{currentProject}</p>
              </div>
            </div>
          )}

          {/* Skills */}
          {skills.length > 0 && (
            <div>
              <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Skills</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {skills.map(skill => (
                  <span
                    key={skill}
                    className="px-2.5 py-1 text-[11px] rounded-lg bg-purple-500/10 text-purple-300 border border-purple-500/20 font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// RECOMMENDED CHALLENGES — Personalized for this user (own profile only)
// ============================================================================

const RecommendedChallenges = ({ userId }) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecommended = async () => {
      try {
        const res = await api.get('/challenges/recommended', { params: { limit: 4 } });
        setChallenges(res.data.challenges || []);
      } catch {
        setChallenges([]);
      }
      setLoading(false);
    };
    fetchRecommended();
  }, [userId]);

  if (loading) return null;
  if (challenges.length === 0) return null;

  return (
    <div className="mt-8 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
          <Target className="w-3.5 h-3.5 text-amber-400" />
          Recommended Challenges
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {challenges.map(c => {
          const title = c.challenge_title || c.challengeTitle || c.title || ''; 
          const responseCount = c.responseCount || 0;
          const isUnanswered = responseCount === 0;
          const projectName = c.project?.name;
          const projectImage = c.project?.image_url || c.project?.imageUrl;
          const reasons = c.matchReasons || {};

          // Derive zone from project for color
          let zoneColor = '#f59e0b';
          let zoneLabel = ''; 
          if (c.zone && ZONES[c.zone]) {
            zoneColor = ZONES[c.zone].color;
            zoneLabel = ZONES[c.zone].shortLabel;
          }
          const ZoneIcon = c.zone ? (ZONE_ICONS[c.zone] || Target) : Target;

          return (
            <a
              key={c.id}
              href={`/dashboard?challenge=${c.id}`}
              className={cn(
                'group flex gap-0 bg-zinc-900/50 border rounded-xl overflow-hidden text-left transition-all duration-200',
                'hover:shadow-lg hover:shadow-black/30 hover:-translate-y-px',
                isUnanswered
                  ? 'border-amber-500/25 hover:border-amber-500/50'
                  : 'border-zinc-800/50 hover:border-zinc-700/60'
              )}
            >
              {/* Thumbnail strip */}
              <div className="w-14 flex-shrink-0 relative overflow-hidden bg-zinc-800/60">
                {projectImage ? (
                  <img src={projectImage} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${zoneColor}18, transparent)` }}>
                    <ZoneIcon className="w-4 h-4" style={{ color: zoneColor, opacity: 0.5 }} />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 p-3">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  {zoneLabel && (
                    <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold"
                      style={{ backgroundColor: `${zoneColor}18`, color: zoneColor }}>
                      {zoneLabel}
                    </span>
                  )}
                  {isUnanswered && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-amber-500/10 text-amber-400">
                      <span className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" />
                      Be first
                    </span>
                  )}
                  {reasons.zoneMatch && (
                    <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-purple-500/10 text-purple-400">
                      Your zone
                    </span>
                  )}
                  {reasons.skillMatches?.length > 0 && (
                    <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-cyan-500/10 text-cyan-400">
                      Skill match
                    </span>
                  )}
                </div>
                <p className="text-[12px] font-semibold text-white group-hover:text-amber-300 transition-colors line-clamp-2 leading-snug">
                  {title}
                </p>
                <div className="flex items-center gap-2 mt-1.5 text-[10px] text-zinc-600">
                  <span className="flex items-center gap-0.5">
                    <Users className="w-2.5 h-2.5" />{responseCount}
                  </span>
                  {projectName && (
                    <span className="truncate max-w-[100px]">{projectName}</span>
                  )}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// FOLLOWERS MODAL
// ============================================================================

const FollowersModal = ({ userId, type, onClose, onNavigate }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/users/${userId}/${type}`);
        setUsers(res.data.users || []);
      } catch (e) {
        console.error('Failed to load', type, e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [userId, type]);
  
  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-sm max-h-[60vh] overflow-hidden pointer-events-auto shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <h3 className="font-semibold text-white capitalize">{type}</h3>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-400">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="overflow-y-auto max-h-[calc(60vh-60px)]">
            {loading ? (
              <div className="flex justify-center p-8"><Spinner /></div>
            ) : users.length === 0 ? (
              <p className="text-center text-zinc-500 p-8 text-sm">
                No {type} yet
              </p>
            ) : (
              <div className="p-2">
                {users.map(u => (
                  <button
                    key={u.id}
                    onClick={() => { onNavigate(u.id); onClose(); }}
                    className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-zinc-800/50 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {u.profilePictureUrl ? (
                        <img src={u.profilePictureUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-zinc-500">
                          {(u.username || '?')[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{u.username}</p>
                      {u.bio && (
                        <p className="text-xs text-zinc-500 truncate">{u.bio}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// MAIN PROFILE PAGE
// ============================================================================

const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const platform = usePlatform();
  
  // Determine which user to show
  const profileId = userId ? parseInt(userId) : currentUser?.id;
  const isOwnProfile = profileId === currentUser?.id;
  
  // State
  const [profile, setProfile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [zoneStats, setZoneStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters — read zone from platform (persists Map ↔ Explore ↔ here)
  // Sort maps: platform 'newest' → API 'recent', platform 'most_forked' → API 'forks', etc.
  const activeZone = platform.filters.activeZones.length > 0
    ? platform.filters.activeZones[0]
    : null;
  const platformSort = platform.filters.sortBy;
  const sortBy = platformSort === 'newest' ? 'recent'
    : platformSort === 'most_forked' ? 'forks'
    : platformSort === 'most_viewed' ? 'views'
    : 'recent';
  
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalProjects, setTotalProjects] = useState(0);
  
  // Modals
  const [showFollowers, setShowFollowers] = useState(null); // 'followers' or 'following'
  
  // Connection stats (the metrics that matter on Enume)
  const [connectionStats, setConnectionStats] = useState(null);
  
  // ---- Fetch Profile ----
  const fetchProfile = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    setError(null);
    
    try {
      const [profileRes, zoneRes, connRes] = await Promise.all([
        api.get(`/users/${profileId}`),
        api.get(`/users/${profileId}/zone-stats`),
        api.get(`/users/${profileId}/connection-stats`).catch(() => ({ data: null })),
      ]);
      
      setProfile(profileRes.data);
      setZoneStats(zoneRes.data);
      if (connRes.data) setConnectionStats(connRes.data);
    } catch (e) {
      console.error('Failed to load profile:', e);
      setError(e.response?.status === 404 ? 'User not found' : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [profileId]);
  
  // ---- Fetch Projects ----
  const fetchProjects = useCallback(async (resetPage = false) => {
    if (!profileId) return;
    setProjectsLoading(true);
    const p = resetPage ? 1 : page;
    
    try {
      const params = { page: p, per_page: 12, sort: sortBy };
      if (activeZone) params.zone = activeZone;
      
      const res = await api.get(`/users/${profileId}/projects`, { params });
      const data = res.data;
      
      if (resetPage || p === 1) {
        setProjects(data.projects);
      } else {
        setProjects(prev => [...prev, ...data.projects]);
      }
      setTotalProjects(data.total);
      setHasMore(data.hasNext);
      if (resetPage) setPage(1);
    } catch (e) {
      console.error('Failed to load projects:', e);
    } finally {
      setProjectsLoading(false);
    }
  }, [profileId, page, sortBy, activeZone]);
  
  // ---- Effects ----
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);
  
  useEffect(() => {
    fetchProjects(true);
  }, [profileId, sortBy, activeZone]);
  
  // ---- Follow/Unfollow ----
  const handleToggleFollow = async () => {
    if (!profileId || isOwnProfile) return;
    setFollowLoading(true);
    try {
      const res = await api.post(`/users/${profileId}/follow`);
      setProfile(prev => ({
        ...prev,
        isFollowing: res.data.isFollowing,
        stats: { ...prev.stats, followerCount: res.data.followerCount }
      }));
    } catch (e) {
      console.error('Follow failed:', e);
    } finally {
      setFollowLoading(false);
    }
  };
  
  // ---- Navigate to project on map ----
  const navigateToProject = (projectId) => {
    navigate(`/dashboard?project=${projectId}`);
  };
  
  const navigateToUser = (uid) => {
    navigate(`/profile/${uid}`);
  };
  
  // ---- Load more ----
  const loadMore = () => {
    setPage(prev => prev + 1);
    fetchProjects(false);
  };
  
    // ---- Zone filter options ----
  // NOTE: these come from GET /users/:id/zone-stats, which counts by the
  // backend's own reckoning. The cards below use getProjectZone(). If the
  // pills ever show a zone with no matching cards, that is the mismatch —
  // the endpoint would need to use zone_config.detect_project_zone().
  const zoneOptions = useMemo(() => {
    return Object.entries(zoneStats)
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([zone]) => zone);
  }, [zoneStats]);
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-500 text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }
  
  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">{error || 'Profile not found'}</h1>
          <p className="text-zinc-400 mb-6">This user may not exist or the page failed to load.</p>
          <Button onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4" /> Back to Map
          </Button>
        </div>
      </div>
    );
  }
  
  const { stats } = profile;
  const displayName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.username;
  const yearLabel = getYearLabel(profile.year);
  
  return (
    <div className="min-h-screen bg-zinc-950">
      
      {/* ============================================================ */}
      {/* HERO SECTION */}
      {/* ============================================================ */}
      <div className="relative">
        {/* Background gradient */}
        <div className="absolute inset-0 h-48 bg-gradient-to-b from-purple-900/20 via-zinc-950 to-zinc-950" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-2">
          
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          
          {/* Profile header */}
          <div className="flex flex-col sm:flex-row items-start gap-5">
            
            {/* Avatar */}
            <div className="relative group">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-zinc-800 border-2 border-zinc-700/50 overflow-hidden shadow-xl shadow-black/30 flex-shrink-0">
                {profile.profilePictureUrl ? (
                  <img 
                    src={profile.profilePictureUrl.startsWith('http') ? profile.profilePictureUrl : `${API_ORIGIN}${profile.profilePictureUrl}`}
                    alt={profile.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-indigo-700">
                    <span className="text-3xl font-bold text-white/90">
                      {(profile.username || '?')[0].toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              {isOwnProfile && (
                <button
                  onClick={() => navigate('/settings')}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                    {displayName}
                  </h1>
                  <p className="text-zinc-500 text-sm mt-0.5">@{profile.username}</p>
                </div>
                
                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isOwnProfile ? (
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => navigate('/settings')}
                    >
                      <Settings className="w-4 h-4" /> Edit Profile
                    </Button>
                  ) : (
                    <>
                      <FollowButton
                        isFollowing={profile.isFollowing}
                        onToggle={handleToggleFollow}
                        loading={followLoading}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/messages?user=${profileId}`)}
                        title="Message"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Bio */}
              {profile.bio && (
                <p className="text-zinc-300 text-sm mt-3 leading-relaxed max-w-lg">
                  {profile.bio}
                </p>
              )}
              
              {/* Meta tags */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-zinc-500">
                {yearLabel && (
                  <span className="flex items-center gap-1">
                    <Award className="w-3 h-3" /> {yearLabel}
                  </span>
                )}
                {profile.createdAt && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Joined {formatDate(profile.createdAt)}
                  </span>
                )}
              </div>
              
              {/* Skills — now shown in Creative Identity card below */}
              {/* Compact zone badges inline with name */}
              {profile.creativeIdentity?.zones?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {profile.creativeIdentity.zones.map(zoneId => {
                    const zone = ZONES[zoneId];
                    if (!zone) return null;
                    const ZoneIcon = ZONE_ICONS[zoneId] || Sparkles;
                    return (
                      <button
                        key={zoneId}
                        onClick={() => navigate(`/zone/${zoneId}`)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all hover:brightness-125 active:scale-[0.97]"
                        style={{ backgroundColor: `${zone.color}15`, color: zone.color }}
                      >
                        <ZoneIcon className="w-3 h-3" />
                        {zone.shortLabel}
                      </button>
                    );
                  })}
                  {profile.creativeIdentity.openToCollab && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-500/10 text-emerald-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      Open to collab
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* ============================================================ */}
          {/* STATS BAR — Connection-centric */}
          {/* ============================================================ */}
          
          {/* Primary connection stats */}
          <div className="mt-6 flex items-center justify-start gap-1 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
            <StatItem icon={Grid} label="Projects" value={stats.projectCount} color="text-purple-400" />
            <div className="w-px h-8 bg-zinc-800 mx-1" />
            <StatItem icon={GitFork} label="Forks" value={stats.forksReceived} color="text-indigo-400" />
            <div className="w-px h-8 bg-zinc-800 mx-1" />
            {connectionStats && (
              <>
                <StatItem icon={Sparkles} label="Zones Reached" value={connectionStats.zonesReached} color="text-cyan-400" />
                <div className="w-px h-8 bg-zinc-800 mx-1" />
                <StatItem icon={Zap} label="Cross-Zone" value={connectionStats.crossZoneForks} color="text-fuchsia-400" />
                <div className="w-px h-8 bg-zinc-800 mx-1" />
                <StatItem icon={TrendingUp} label="Deepest Chain" value={connectionStats.deepestChain} color="text-amber-400" />
              </>
            )}
            {!connectionStats && (
              <>
                <StatItem icon={Eye} label="Views" value={stats.totalViews} color="text-cyan-400" />
              </>
            )}
            {stats.challengeResponses > 0 && (
              <>
                <div className="w-px h-8 bg-zinc-800 mx-1" />
                <StatItem icon={CheckCircle} label="Challenges" value={stats.challengeResponses} color="text-emerald-400" />
              </>
            )}
          </div>
          
          {/* Connection Score badge (when available) */}
          {connectionStats && connectionStats.connectionScore > 0 && (
            <div className="mt-3 flex items-center gap-3">
              <div className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border',
                connectionStats.connectionScore >= 500
                  ? 'bg-purple-500/10 border-purple-500/25'
                  : connectionStats.connectionScore >= 200
                  ? 'bg-cyan-500/10 border-cyan-500/25'
                  : 'bg-zinc-800/50 border-zinc-700/40'
              )}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  className={connectionStats.connectionScore >= 500 ? 'text-purple-400' : connectionStats.connectionScore >= 200 ? 'text-cyan-400' : 'text-zinc-500'}>
                  <path d="M12 2L2 12l10 10 10-10L12 2z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span className={cn(
                  'text-sm font-bold tabular-nums',
                  connectionStats.connectionScore >= 500 ? 'text-purple-300' : connectionStats.connectionScore >= 200 ? 'text-cyan-300' : 'text-zinc-400'
                )}>
                  {connectionStats.connectionScore}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Connection Score</span>
              </div>
              
              {/* Secondary: followers (still accessible, just not the headline) */}
              <button 
                onClick={() => setShowFollowers('followers')}
                className="text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                {formatNumber(stats.followerCount)} followers
              </button>
              <span className="text-zinc-800">&middot;</span>
              <button 
                onClick={() => setShowFollowers('following')}
                className="text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                {formatNumber(stats.followingCount)} following
              </button>
            </div>
          )}
          
          {/* Fallback: show followers when no connection stats */}
          {!connectionStats && (
            <div className="mt-3 flex items-center gap-3">
              <button 
                onClick={() => setShowFollowers('followers')}
                className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {formatNumber(stats.followerCount)} followers
              </button>
              <span className="text-zinc-800">&middot;</span>
              <button 
                onClick={() => setShowFollowers('following')}
                className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {formatNumber(stats.followingCount)} following
              </button>
            </div>
          )}
          
          {/* Zone distribution */}
          <div className="mt-5">
            <ZoneBar zoneStats={zoneStats} totalProjects={stats.projectCount} />
          </div>

          {/* ============================================================ */}
          {/* CREATIVE IDENTITY */}
          {/* ============================================================ */}
          <CreativeIdentityCard
            identity={profile.creativeIdentity}
            isOwnProfile={isOwnProfile}
            onEdit={() => navigate('/settings?tab=identity')}
            connectionStats={connectionStats}
          />

          {/* ============================================================ */}
          {/* INFLUENCE GRAPH — Radial descendant visualization */}
          {/* ============================================================ */}
          <InfluenceGraph
            userId={profileId}
            isOwnProfile={isOwnProfile}
          />
        </div>
      </div>
      
      {/* ============================================================ */}
      {/* RECOMMENDED CHALLENGES (own profile only) */}
      {/* ============================================================ */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {isOwnProfile && profile.creativeIdentity?.zones?.length > 0 && (
          <RecommendedChallenges userId={profileId} />
        )}
      </div>

      {/* ============================================================ */}
      {/* PROJECTS SECTION */}
      {/* ============================================================ */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-8 pb-24">
        
        {/* Section header with filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
          <h2 className="text-lg font-bold text-white">
            Projects
            {totalProjects > 0 && (
              <span className="text-zinc-600 font-normal text-sm ml-2">{totalProjects}</span>
            )}
          </h2>
          
          <div className="flex items-center gap-2">
            {/* Zone filter pills — wired to PlatformContext */}
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => {
                  // Clear all zone filters to show "All"
                  if (platform.filters.activeZones.length > 0) {
                    platform.filters.activeZones.forEach(z => platform.filters.toggleZone(z));
                  }
                }}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap',
                  !activeZone 
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                )}
              >
                All
              </button>
              {zoneOptions.map(zone => {
                const zoneConfig = ZONES[zone];
                const isActive = activeZone === zone;
                return (
                  <button
                    key={zone}
                    onClick={() => {
                      // Clear other zones first (single-select behavior on Profile)
                      platform.filters.activeZones.forEach(z => {
                        if (z !== zone) platform.filters.toggleZone(z);
                      });
                      platform.filters.toggleZone(zone);
                    }}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap',
                      isActive
                        ? 'border'
                        : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                    )}
                    style={isActive ? {
                      backgroundColor: `${zoneConfig?.color}15`,
                      color: zoneConfig?.color,
                      borderColor: `${zoneConfig?.color}40`,
                    } : {}}
                  >
                    {zoneConfig?.shortLabel || zone}
                  </button>
                );
              })}
            </div>
            
            {/* Sort — maps to platform.filters.setSortBy */}
            <select
              value={platformSort}
              onChange={(e) => platform.filters.setSortBy(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500/30 cursor-pointer"
            >
              <option value="newest">Newest</option>
              <option value="most_viewed">Most Viewed</option>
              <option value="most_forked">Most Forked</option>
            </select>
          </div>
        </div>
        
        {/* Projects Grid */}
        {projectsLoading && projects.length === 0 ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-zinc-800/50 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-zinc-600" />
            </div>
            <h3 className="text-white font-semibold mb-1">
              {activeZone ? 'No projects in this zone' : 'No projects yet'}
            </h3>
            <p className="text-zinc-500 text-sm max-w-xs mx-auto">
              {isOwnProfile 
                ? 'Head to the map and create your first project to start building your creative portfolio.'
                : 'This user hasn\'t published any projects yet.'}
            </p>
            {isOwnProfile && (
              <Button 
                variant="primary" 
                className="mt-4"
                onClick={() => navigate('/dashboard?create=true')}
              >
                <Plus className="w-4 h-4" /> Create First Project
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map(project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onNavigate={navigateToProject}
                />
              ))}
            </div>
            
            {/* Load more */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="secondary"
                  onClick={loadMore}
                  loading={projectsLoading}
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* ============================================================ */}
      {/* MODALS */}
      {/* ============================================================ */}
      {showFollowers && (
        <FollowersModal
          userId={profileId}
          type={showFollowers}
          onClose={() => setShowFollowers(null)}
          onNavigate={navigateToUser}
        />
      )}
    </div>
  );
};

export default ProfilePage;